import { ColorEditorV16 } from '@/components/UI/SpreadSheet/editors-v16/ColorEditorV16';
import { DateEditorWrapperV16 } from '@/components/UI/SpreadSheet/editors-v16/DateEditorWrapperV16';
import { ExponentialEditorV16 } from '@/components/UI/SpreadSheet/editors-v16/ExponentialEditorV16';
import { MultiSelectEditorV16 } from '@/components/UI/SpreadSheet/editors-v16/MultiSelectEditorV16';

import { _isEmpty, _isNil, _set } from '@/littledash';
import type { MetadataField } from '@/model/Metadata.model';
import { DateInputUtils, DateUtils } from '@/utils/Date.utils';
import { createHotRequiredValidator, textValidatorFactory } from '@/validators';

import type { TreatmentGroupKeyedMetadata } from './CreateTreatmentGroups';
import type { ReactNode } from 'react';
import { subjectValidator } from './CreateTreatmentGroups.utils';
import type { TreatmentGroup } from '@/model/TreatmentGroup.model';

interface ColumnDataResult {
  colHeaders: Array<string>;
  columns: Array<Record<string, unknown>>;
}

interface ColumnSettingMapperResult {
  columnHeader: string;
  columnSettings: Record<string, unknown>;
}

const requiredColumn = (title: string) => `${title} <span class="required">(req.)</span>`;
const inactiveColumn = (title: string) => `${title} <span class="required">(archived)</span>`;

const dateValidator =
  (required: boolean) =>
  (value: unknown, callback: (valid: boolean) => void): void =>
    callback((_isEmpty(value) && !required) || DateUtils.isValidDate(value as string));

const columnDataGetterSetterFactory =
  (metadata: MetadataField) =>
  (row: TreatmentGroupKeyedMetadata, value?: unknown): unknown => {
    if (_isNil(row?.metadata?.[metadata.id])) {
      _set(row, ['metadata', metadata.id], {
        ...metadata,
        glossary_id: metadata.id,
        value,
      });
    }
    if (typeof value === 'undefined') {
      return row?.metadata?.[metadata.id]?.value ?? '';
    }
    _set(row, ['metadata', metadata.id], {
      ...metadata,
      glossary_id: metadata.id,
      value,
    });
    _set(row, ['updated_at'], new Date().toISOString());
  };

const selectColumnDataGetterSetterFactory =
  (metadata: MetadataField) =>
  (row: TreatmentGroupKeyedMetadata, value?: string): unknown => {
    if (_isNil(row?.metadata?.[metadata.id])) {
      _set(row, ['metadata', metadata.id], {
        ...metadata,
        glossary_id: metadata.id,
        value: value?.trim() ?? null,
      });
    }
    if (typeof value === 'undefined') {
      return row?.metadata?.[metadata.id]?.value ?? null;
    }
    _set(row, ['metadata', metadata.id], {
      ...metadata,
      glossary_id: metadata.id,
      value: value,
    });
    _set(row, ['updated_at'], new Date().toISOString());
  };

const multiSelectColumnDataGetterSetterFactory =
  (metadata: MetadataField) =>
  (row: TreatmentGroupKeyedMetadata, value?: string): unknown => {
    if (_isNil(row?.metadata?.[metadata.id])) {
      _set(row, ['metadata', metadata.id], {
        ...metadata,
        glossary_id: metadata.id,
        value: value?.split(',')?.map((v: string) => v.trim()) ?? [],
      });
    }
    if (typeof value === 'undefined') {
      const metadataValue = row?.metadata?.[metadata.id]?.value ?? [];
      return Array.isArray(metadataValue) ? metadataValue.join(',') : '';
    }
    _set(row, ['metadata', metadata.id], {
      ...metadata,
      glossary_id: metadata.id,
      value: typeof value === 'string' ? (value.split(',') ?? []) : [],
    });
    _set(row, ['updated_at'], new Date().toISOString());
  };

export const subjectValidatorMessageV16 = (value: ReactNode, rowData?: unknown): string => {
  if (_isNil(value) || value === '') {
    return 'Required field';
  }
  if (typeof value !== 'number') {
    return `${value} must be a number`;
  }
  if (value < 0 || value > 100 || !Number.isInteger(value)) {
    return 'Max. subjects is required and must be an integer value lower than 100';
  }
  const animalCount = (rowData as TreatmentGroup)?.animals_count ?? 0;
  if (value < animalCount) {
    return `There are ${animalCount} animals assigned to this group. Enter a higher number to continue.`;
  }
  return 'Required field';
};

// V16 Initial Data - Cast to match v12 pattern (partial initial state populated at runtime)
export const initialDataV16 = [
  {
    color: ColorEditorV16.colorForIndex(0),
    name: 'Group 1',
    max_subjects: 0,
  },
] as unknown as Array<TreatmentGroupKeyedMetadata>;

const baseColumnDataV16: ColumnDataResult = {
  colHeaders: [requiredColumn('Color'), requiredColumn('Name'), requiredColumn('No. of animals')],
  columns: [
    {
      data: 'color',
      value: 'Color',
      type: ColorEditorV16.type,
      width: 110,
    },
    {
      data: 'name',
      value: 'Name',
      type: 'text',
      width: 200,
      validator: createHotRequiredValidator('Name', true, textValidatorFactory(255)),
    },
    {
      data: 'max_subjects',
      value: 'Max Subjects',
      type: 'numeric',
      width: 165,
      validator: subjectValidator(),
      validationErrorMessage: subjectValidatorMessageV16,
    },
  ],
};

export const settingsV16 = {
  ...baseColumnDataV16,
  columnSorting: false,
  className: 'htMiddle',
  minSpareRows: 1,
  manualColumnResize: true,
  rowHeights: 50,
  height: 500,
  rowHeaders: true,
  outsideClickDeselects: false,
};

export const metadataToColumnSettingMapperV16 = (metadata: MetadataField): ColumnSettingMapperResult => {
  const metaID = metadata.id;
  switch (metadata.field_type) {
    case 'select': {
      return {
        columnHeader: metadata.title,
        columnSettings: {
          data: selectColumnDataGetterSetterFactory(metadata),
          metaID,
          width: 200,
          type: MultiSelectEditorV16.type,
          options: metadata.options,
          isMultiSelect: false,
          validator: MultiSelectEditorV16.validatorFactory(metadata.options ?? [], false, metadata.required ? 1 : 0, 1),
        },
      };
    }
    case 'lookup_select': {
      return {
        columnHeader: metadata.title,
        columnSettings: {
          data: selectColumnDataGetterSetterFactory(metadata),
          metaID,
          width: 200,
          type: MultiSelectEditorV16.type,
          optionsLookupId: metadata.id,
          required: metadata.required === true,
          isMultiSelect: false,
        },
      };
    }
    case 'multi_select': {
      return {
        columnHeader: metadata.title,
        columnSettings: {
          data: multiSelectColumnDataGetterSetterFactory(metadata),
          metaID,
          width: 200,
          type: MultiSelectEditorV16.type,
          options: metadata.options,
          validator: MultiSelectEditorV16.validatorFactory(metadata.options ?? [], true, metadata.required ? 1 : 0),
        },
      };
    }
    case 'lookup_multi_select': {
      return {
        columnHeader: metadata.title,
        columnSettings: {
          data: multiSelectColumnDataGetterSetterFactory(metadata),
          metaID,
          width: 200,
          type: MultiSelectEditorV16.type,
          optionsLookupId: metadata.id,
          required: metadata.required === true,
        },
      };
    }
    case 'text': {
      return {
        columnHeader: metadata.title,
        columnSettings: {
          data: columnDataGetterSetterFactory(metadata),
          metaID,
          width: 200,
          type: 'text',
          metadataType: 'text',
          validator: createHotRequiredValidator(metadata.title, metadata.required, textValidatorFactory()),
        },
      };
    }
    case 'date': {
      return {
        columnHeader: metadata.title,
        columnSettings: {
          data: columnDataGetterSetterFactory(metadata),
          metaID,
          width: 150,
          correctFormat: true,
          type: DateEditorWrapperV16.type,
          dateFormat: DateInputUtils.hotDatePattern,
          metadataType: 'date',
          validator: dateValidator(metadata.required),
        },
      };
    }
    case 'numeric': {
      return {
        columnHeader: metadata.title,
        columnSettings: {
          data: columnDataGetterSetterFactory(metadata),
          metaID,
          width: 150,
          type: ExponentialEditorV16.type,
          validator: ExponentialEditorV16.numericValidatorFactory(metadata.required),
        },
      };
    }
    default: {
      throw new Error(`Unrecognised metadata field_type [${metadata.field_type}]`);
    }
  }
};

export const generateColumnDataV16 = (
  displayedGroupMetadata: Array<Partial<MetadataField> & { required?: boolean }>
): ColumnDataResult =>
  displayedGroupMetadata.reduce<ColumnDataResult>(
    (acc, metadata) => {
      if (metadata.field_type) {
        const { columnHeader, columnSettings } = metadataToColumnSettingMapperV16(metadata as MetadataField);
        let header = columnHeader;
        if (metadata.active === false) {
          header = inactiveColumn(columnHeader);
        } else if (metadata.required) {
          header = requiredColumn(columnHeader);
        }

        return {
          colHeaders: [...acc.colHeaders, header],
          columns: [...acc.columns, columnSettings],
        };
      }
      return acc;
    },
    { colHeaders: [...baseColumnDataV16.colHeaders], columns: [...baseColumnDataV16.columns] }
  );
